import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser]           = useState(null);
  const [profile, setProfile]     = useState(null);
  const [authStage, setAuthStage] = useState('loading');

  // ── helpers ───────────────────────────────────────────────────────────────

  async function fetchProfileData(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    setProfile(data);
    return data;
  }

  async function determineAuthStage(session, profileData) {
    if (!session) return 'unauthenticated';

    // 2FA desativado — remover este bloco para reativar
    // const { data: factorsData } = await supabase.auth.mfa.listFactors();
    // const totpFactors = factorsData?.totp ?? [];
    // if (totpFactors.length === 0) return 'mfa_enroll';
    // const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    // if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') return 'mfa_verify';

    if (!profileData?.aprovado) return 'pending_approval';

    return 'ready';
  }

  // ── listener central ──────────────────────────────────────────────────────

  useEffect(() => {
    async function handleSession(session) {
      if (!session) {
        setUser(null);
        setProfile(null);
        setAuthStage('unauthenticated');
        return;
      }
      setUser(session.user);
      const profileData = await fetchProfileData(session.user.id);
      const stage = await determineAuthStage(session, profileData);
      setAuthStage(stage);
    }

    // getSession() garante a inicialização mesmo sem INITIAL_SESSION event
    supabase.auth.getSession().then(({ data: { session } }) => handleSession(session));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'INITIAL_SESSION') return; // já tratado pelo getSession acima
        handleSession(session);
      }
    );

    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── auth actions ──────────────────────────────────────────────────────────

  async function signIn(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
    // onAuthStateChange cuida do restante
  }

  async function signUp(email, password, nomeCompleto, cpf) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { nome_completo: nomeCompleto, cpf } },
    });
    if (error) return { error };

    // Auto-confirma o email via Edge Function (confirmação por email desativada)
    if (data?.user?.id) {
      await supabase.functions.invoke('auto-confirm-email', {
        body: { user_id: data.user.id },
      });
    }

    return { error: null };
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  // ── MFA actions ───────────────────────────────────────────────────────────

  async function enrollMfa() {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) throw error;
    return data;
  }

  async function challengeMfa(factorId) {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId });
    if (error) throw error;
    return data;
  }

  async function verifyMfa(factorId, challengeId, code) {
    const { error } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
    if (error) throw error;
    // MFA_CHALLENGE_VERIFIED dispara onAuthStateChange automaticamente
  }

  const verifyMfaEnrollment = verifyMfa;

  async function getMfaFactors() {
    const { data } = await supabase.auth.mfa.listFactors();
    return data?.totp ?? [];
  }

  // ── admin actions ─────────────────────────────────────────────────────────

  async function approveUser(userId) {
    const { error } = await supabase
      .from('profiles')
      .update({ aprovado: true, aprovado_em: new Date().toISOString() })
      .eq('id', userId);
    if (error) throw error;
  }

  async function recheckApproval() {
    if (!user) return;
    const { data: { session } } = await supabase.auth.getSession();
    const profileData = await fetchProfileData(user.id);
    const stage = await determineAuthStage(session, profileData);
    setAuthStage(stage);
  }

  // ── derived ───────────────────────────────────────────────────────────────

  const loading      = authStage === 'loading';
  const isSuperAdmin = profile?.role === 'super_admin';
  const isAdmin      = ['super_admin', 'admin_pesquisa'].includes(profile?.role);
  const isPesquisador = ['super_admin', 'admin_pesquisa', 'pesquisador'].includes(profile?.role);

  return (
    <AuthContext.Provider value={{
      user, profile, authStage, loading,
      signIn, signUp, signOut,
      enrollMfa, challengeMfa, verifyMfa, verifyMfaEnrollment, getMfaFactors,
      approveUser, recheckApproval,
      isSuperAdmin, isAdmin, isPesquisador,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
